import { useEffect, useRef } from 'react'

// Brand mark — five shapes that morph through a metaball / goo filter.
// Order tells a story: the flowing-terrain logo opens, then find a
// house, drop a pin on it, plant a tree, measure the yield as a
// chart, back to the logo. The goo filter (Gaussian blur followed by
// an alpha threshold) keeps the transition reading as one liquid
// mass rather than a crossfade.
//
// Animation timing follows the brand spec — slow, contemplative,
// breathing pace. A 2.4 s hold lets each shape land before the 3.4 s
// transition begins, and the blur follows a sine bell curve (not a
// triangle) so its rise + fall is smooth at the endpoints. The CSS
// opacity transition uses the same 3.4 s duration with a symmetric
// ease so the shape fade and the blur ramp share an envelope.
//
// Gradient stops point at the active palette's --grad-bg-* tokens so
// flipping palette from the admin page repaints the mark alongside
// the rest of the brand surface — no hard-coded hex anywhere.

// Flowing terrain / wave silhouette. Source viewBox 712.24 × 363.94;
// rendered inside the 200 × 200 morph stage via a translate + uniform
// scale (scale 0.281, translate y 48.85) so it fills the stage width
// and centres vertically alongside the other shapes.
const LOGO_PATH =
  'M448.07,188.65c-49.95,17.13-97.96,44.12-141.66,73.15-20.64,13.71-39.96,28.1-59.66,42.96-4.31,3.25-9.11,5.55-14.06,6.93-7.05,1.97-13.06-1.92-15.16-8.87-2.69-8.89-2.53-18.18-1.3-27.82,3.11-24.29,10.46-47.43,21.19-69.34,4.63-9.45,10.51-16.93,18.16-24.1,19.55-18.33,40.93-33.61,63.56-48.39-22.42,7.88-42.5,18.91-63.07,30.28-31.73,18.34-62.06,38.71-91.63,60.57C106.49,266.85,52.43,313.15,0,363.94l15.7-100.37c2.91-18.61,6.7-36.12,10.85-54.34,2.21-6.94,3.32-13.99,6.9-20.33,6.86-12.14,30.7-31.77,42.4-40.71,52.07-39.78,124.28-81.42,183.18-110.72,26.2-13.03,52.58-24.38,80.08-33.87,11.07-3.82,28.42-7.65,34.03,5.73,1.61,3.84,2.31,7.98,2.99,12.21.23,30.62-5.9,59.35-11.09,89.69,37.1-16.16,89.62-38.27,127.9-43.93,11.14-1.65,23.58-1.83,26.09,11.58.56,6.81.27,13.41-1.01,20.33-4.13,22.23-8.09,43.99-10.55,66.7,32.46-10.23,63.77-17.73,96.63-22.74,35.79-5.46,71.5-8.63,108.13-8.21-114.53,31.2-214.6,91.16-301.9,170.55-5.48,4.99-22.63,18.12-26.71,6.05s.39-34.66,3.54-47.02l7.16-28.04c2.75-6.15,6.41-11.27,10.93-16.34,12.3-13.12,27.17-22.48,42.81-31.52Z'

const HOUSE_PATH = 'M100,28 L180,92 L180,178 L20,178 L20,92 Z'
const PIN_PATH =
  'M100,14 C137,14 167,44 167,81 C167,114 130,150 100,188 C70,150 33,114 33,81 C33,44 63,14 100,14 Z M100,58 a22,22 0 1,0 0.01,0 Z'
const TREE_PATH =
  'M100,18 L150,76 L128,76 L162,124 L132,124 L158,166 L114,166 L114,186 L86,186 L86,166 L42,166 L68,124 L38,124 L72,76 L50,76 Z'
// Five-bar chart sitting on a thin baseline. Bar widths 28, gaps 8,
// centred in the 200 × 200 viewBox so each column shares the same
// baseline at y=170. Heights vary so the silhouette reads as data.
const CHART_PATH = [
  'M10,170 L190,170 L190,180 L10,180 Z',
  'M14,130 L42,130 L42,170 L14,170 Z',
  'M50,80 L78,80 L78,170 L50,170 Z',
  'M86,50 L114,50 L114,170 L86,170 Z',
  'M122,100 L150,100 L150,170 L122,170 Z',
  'M158,70 L186,70 L186,170 L158,170 Z',
].join(' ')

const ORDER = ['logo', 'house', 'pin', 'tree', 'chart'] as const
type ShapeName = (typeof ORDER)[number]

const HOLD_MS = 2400
const TRANS_MS = 3400
const FADE_MS = 3400
const BLUR_PEAK = 6
const SHAPE_TRANSITION = `opacity ${FADE_MS}ms cubic-bezier(.45,.05,.55,.95)`

// Unique-id factory so multiple marks on the same page don't share
// SVG filter / gradient ids (Chromium dedupes them by id and the
// second instance otherwise breaks).
let MARK_COUNTER = 0
function newUid(): string {
  MARK_COUNTER += 1
  return `mk${MARK_COUNTER}`
}

export function MorphingMark({
  size = 56,
  className,
}: {
  size?: number
  className?: string
}) {
  const uidRef = useRef<string>(newUid())
  const uid = uidRef.current
  const blurRef = useRef<SVGFEGaussianBlurElement | null>(null)
  const shapeRefs = useRef<Record<ShapeName, SVGElement | null>>({
    logo: null,
    house: null,
    pin: null,
    tree: null,
    chart: null,
  })

  useEffect(() => {
    // Initial state — no transition during setup so opacity flips
    // don't fade-in during mount, then enable the slow ease for
    // every subsequent crossfade.
    ORDER.forEach((name, i) => {
      const el = shapeRefs.current[name]
      if (!el) return
      el.style.transition = 'none'
      el.style.opacity = i === 0 ? '1' : '0'
    })
    let raf = requestAnimationFrame(() => {
      ORDER.forEach((name) => {
        const el = shapeRefs.current[name]
        if (el) el.style.transition = SHAPE_TRANSITION
      })
      raf = requestAnimationFrame(tick)
    })

    let idx = 0
    let phaseStart = performance.now()
    let inTrans = false
    let lastBlur = -1

    function setBlur(v: number) {
      // Quantise to 0.1 so we don't thrash the SVG filter every frame
      // with imperceptible float deltas (the source of the "shake"
      // some browsers showed on the previous tighter timing).
      const q = Math.round(v * 10) / 10
      if (q === lastBlur) return
      lastBlur = q
      blurRef.current?.setAttribute('stdDeviation', String(q))
    }

    function tick(now: number) {
      const elapsed = now - phaseStart
      if (!inTrans) {
        if (elapsed >= HOLD_MS) {
          // Hand off the visible shape: cur fades out, next fades in
          inTrans = true
          phaseStart = now
          const cur = ORDER[idx]
          const next = ORDER[(idx + 1) % ORDER.length]
          const curEl = shapeRefs.current[cur]
          const nextEl = shapeRefs.current[next]
          if (curEl) curEl.style.opacity = '0'
          if (nextEl) nextEl.style.opacity = '1'
        } else {
          setBlur(0)
        }
      } else {
        // Sine bell envelope (0 → 1 → 0) — smooth derivatives at the
        // endpoints, no corner / threshold pop on entry or exit.
        const t = Math.min(1, elapsed / TRANS_MS)
        const bell = Math.sin(t * Math.PI)
        setBlur(bell * BLUR_PEAK)
        if (t >= 1) {
          idx = (idx + 1) % ORDER.length
          inTrans = false
          phaseStart = now
          setBlur(0)
        }
      }
      raf = requestAnimationFrame(tick)
    }

    return () => cancelAnimationFrame(raf)
  }, [])

  const gradId = `${uid}-grad`
  const gooId = `${uid}-goo`
  const fill = `url(#${gradId})`

  return (
    <svg
      className={className}
      style={{ width: size, height: size, overflow: 'visible', display: 'block' }}
      viewBox="0 0 200 200"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--grad-bg-1)" />
          <stop offset="35%" stopColor="var(--grad-bg-2)" />
          <stop offset="55%" stopColor="var(--grad-bg-3)" />
          <stop offset="78%" stopColor="var(--grad-bg-4)" />
          <stop offset="100%" stopColor="var(--grad-bg-5)" />
        </linearGradient>
        <filter
          id={gooId}
          x="-25%"
          y="-25%"
          width="150%"
          height="150%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur ref={blurRef} in="SourceGraphic" stdDeviation={0} result="blur" />
          <feColorMatrix
            in="blur"
            mode="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10"
            result="goo"
          />
          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
        </filter>
      </defs>
      <g filter={`url(#${gooId})`}>
        {/* The logo lives in a 712.24 × 363.94 viewBox; wrap it in a
            translate + uniform scale so it fills the 200 × 200 stage
            horizontally and centres vertically alongside the other
            shapes. Scale 0.281 = 200 / 712.24; translate y 48.85 =
            (200 − 363.94 × 0.281) / 2. */}
        <g
          transform="translate(0, 48.85) scale(0.281)"
          ref={(el) => { shapeRefs.current.logo = el }}
        >
          <path d={LOGO_PATH} fill={fill} />
        </g>
        <path
          ref={(el) => { shapeRefs.current.house = el }}
          d={HOUSE_PATH}
          fill={fill}
        />
        <path
          ref={(el) => { shapeRefs.current.pin = el }}
          d={PIN_PATH}
          fill={fill}
          fillRule="evenodd"
        />
        <path
          ref={(el) => { shapeRefs.current.tree = el }}
          d={TREE_PATH}
          fill={fill}
        />
        <path
          ref={(el) => { shapeRefs.current.chart = el }}
          d={CHART_PATH}
          fill={fill}
        />
      </g>
    </svg>
  )
}
